import URI from 'urijs'
import Vue from 'vue'
import { decamelizeKeys } from 'humps'
import { config } from '@/plugins/env.config'
import { camelToSnake } from '@/utils/rcm.general.utils'

async function getAccounts(filters, attemptNumber = 1) {
  const getAccountUri = new URI(config.apiBaseUrl)
  getAccountUri.segment('rcm')
  getAccountUri.segment('v1')
  getAccountUri.segment('accounts')

  if (filters.statuses) {
    getAccountUri.addSearch('statuses', filters.statuses)
  }
  if (filters.codeSearchValue) {
    getAccountUri.addSearch('search_value', filters.codeSearchValue)
  }

  if (filters.statusHealthcare) {
    getAccountUri.addSearch('healthcare_plan_coverages', filters.statusHealthcare)
  }

  if (filters.createdFrom) {
    getAccountUri.addSearch('from_date', filters.createdFrom)
  }

  if (filters.createdTo) {
    getAccountUri.addSearch('to_date', filters.createdTo)
  }

  if (filters.statusAccount) {
    getAccountUri.addSearch('statuses', filters.statusAccount)
  }

  if (filters.statusDocumentation) {
    getAccountUri.addSearch('required_support_documents_completed', filters.statusDocumentation)
  }

  if (filters.payer) {
    getAccountUri.addSearch('payer', filters.payer)
  }

  if (filters.isConcurrent) {
    getAccountUri.addSearch('concurrent_audit_required', filters.isConcurrent)
  }

  if (filters.minAmount) {
    getAccountUri.addSearch('invoice_amount_min', filters.minAmount)
  }

  if (filters.maxAmount) {
    getAccountUri.addSearch('invoice_amount_max', filters.maxAmount)
  }

  if(filters.serviceTypes){
    getAccountUri.addSearch('service_types', filters.serviceTypes.join(','))
  }

  if (filters.executorServices) {
    getAccountUri.addSearch('executor_service', filters.executorServices)
  }

  if (filters.agreementPlanTypes) {
    getAccountUri.addSearch('agreement_plan_type', filters.agreementPlanTypes)
  }

  if (filters.invoiceSources) {
    getAccountUri.addSearch('invoice_source', filters.invoiceSources)
  }

	if (filters.providerAgreements) {
		getAccountUri.addSearch('agreement_code', filters.providerAgreements)
	}

  if (filters.sortOption) {
    getAccountUri.addSearch('sort', filters.sortOption.sort ? 'desc' : 'asc')
    getAccountUri.addSearch('order_by', camelToSnake(filters.sortOption.orderBy))
  }

  if(filters.missingSupports) {
    getAccountUri.addSearch('missing_supports', filters.missingSupports.join(','))
  }

  if(filters.responsibleAssigned) {
    getAccountUri.addSearch('responsible_email_addresses', filters.responsibleAssigned.join(','))
  }

  if (filters.reassignedAccount) {
    getAccountUri.addSearch('is_reassigned_account', filters.reassignedAccount)
  }

  if (filters.reassignedAccountId) {
    getAccountUri.addSearch('reassigned_account_id', filters.reassignedAccountId)
  }

  getAccountUri.addSearch('page', filters.page)
  getAccountUri.addSearch('size', filters.size)

  try {
    return await Vue.prototype.$http.get(getAccountUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAccounts(attemptNumber + 1)
    }

    throw error
  }
}

async function getRolePermissionsStatuses(providerSlug, attemptNumber = 1) {
  const getRolePermissionStatusesUri = new URI(config.apiBaseUrl)
  getRolePermissionStatusesUri.segment('rcm')
  getRolePermissionStatusesUri.segment('v1')
  getRolePermissionStatusesUri.segment('role-permissions')
  getRolePermissionStatusesUri.segment('providers')
  getRolePermissionStatusesUri.segment(providerSlug)
  getRolePermissionStatusesUri.segment('statuses')

  try {
    return await Vue.prototype.$http.get(getRolePermissionStatusesUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status == 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getRolePermissionsStatuses(providerSlug, attemptNumber + 1)
    }

    throw error
  }
}

async function getRolePermissionsAccountRestrictedEvents(
  providerSlug,
  accountId,
  attemptNumber = 1
) {
  const getRolePermissionAccountRestrictedEventsUri = new URI(config.apiBaseUrl)
  getRolePermissionAccountRestrictedEventsUri.segment('rcm')
  getRolePermissionAccountRestrictedEventsUri.segment('v1')
  getRolePermissionAccountRestrictedEventsUri.segment('role-permissions')
  getRolePermissionAccountRestrictedEventsUri.segment('providers')
  getRolePermissionAccountRestrictedEventsUri.segment(providerSlug)
  getRolePermissionAccountRestrictedEventsUri.segment('accounts')
  getRolePermissionAccountRestrictedEventsUri.segment(accountId)
  getRolePermissionAccountRestrictedEventsUri.segment('restricted-events')

  try {
    return await Vue.prototype.$http.get(getRolePermissionAccountRestrictedEventsUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status == 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getRolePermissionsAccountRestrictedEvents(providerSlug, attemptNumber + 1)
    }

    throw error
  }
}

async function getRolePermissionsSupportFileRestrictions(
  providerSlug,
  accountId,
  attemptNumber = 1
) {
  const getRolePermissionSupportFileRestrictionsUri = new URI(config.apiBaseUrl)
  getRolePermissionSupportFileRestrictionsUri.segment('rcm')
  getRolePermissionSupportFileRestrictionsUri.segment('v1')
  getRolePermissionSupportFileRestrictionsUri.segment('role-permissions')
  getRolePermissionSupportFileRestrictionsUri.segment('providers')
  getRolePermissionSupportFileRestrictionsUri.segment(providerSlug)
  getRolePermissionSupportFileRestrictionsUri.segment('accounts')
  getRolePermissionSupportFileRestrictionsUri.segment(accountId)
  getRolePermissionSupportFileRestrictionsUri.segment('support-file-restrictions')

  try {
    return await Vue.prototype.$http.get(getRolePermissionSupportFileRestrictionsUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status == 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getRolePermissionsSupportFileRestrictions(providerSlug, attemptNumber + 1)
    }

    throw error
  }
}

async function getAccountCharges(accountId, filters, attemptNumber = 1) {
  const getAccountChargesUri = new URI(config.apiBaseUrl)
  getAccountChargesUri.segment('rcm')
  getAccountChargesUri.segment('v1')
  getAccountChargesUri.segment('accounts')
  getAccountChargesUri.segment(accountId)
  getAccountChargesUri.segment('charges')

  if (filters.serviceTypes) {
    getAccountChargesUri.addSearch('service_types', filters.serviceTypes)
  }

  if (filters.requiredSupportDocumentsCompleted) {
    getAccountChargesUri.addSearch(
      'required_support_documents_completed',
      filters.requiredSupportDocumentsCompleted
    )
  }

  if (filters.fromDate) {
    getAccountChargesUri.addSearch('from_date', filters.fromDate)
  }

  if (filters.toDate) {
    getAccountChargesUri.addSearch('to_date', filters.toDate)
  }

  if (filters.searchValue) {
    getAccountChargesUri.addSearch('search_value', filters.searchValue)
  }

  getAccountChargesUri.addSearch('page', filters.page)
  getAccountChargesUri.addSearch('size', filters.size)

  try {
    return await Vue.prototype.$http.get(getAccountChargesUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status == 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAccountCharges(accountId, attemptNumber + 1)
    }

    throw error
  }
}

async function getDetailAccount(accountId, attemptNumber = 1) {
  const getAccountUri = new URI(config.apiBaseUrl)
  getAccountUri.segment('rcm')
  getAccountUri.segment('v1')
  getAccountUri.segment('accounts')
  getAccountUri.segment(accountId)

  try {
    return await Vue.prototype.$http.get(getAccountUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAccounts(attemptNumber + 1)
    }

    throw error
  }
}

async function getAllSupportFiles(accountId, filters, attemptNumber = 1) {
  const getAllSupportFilesUri = new URI(config.apiBaseUrl)
  getAllSupportFilesUri.segment('rcm')
  getAllSupportFilesUri.segment('v1')
  getAllSupportFilesUri.segment('accounts')
  getAllSupportFilesUri.segment(accountId)
  getAllSupportFilesUri.segment('get-all-support-files')

  if (filters.relation) {
    getAllSupportFilesUri.addSearch('relations', filters.relation)
  }

  if (filters.statuses) {
    getAllSupportFilesUri.addSearch('statuses', filters.statuses)
  }

  if (filters.searchValue) {
    getAllSupportFilesUri.addSearch('search_value', filters.searchValue)
  }

  if(filters.code){
    getAllSupportFilesUri.addSearch('code', filters.code)
  }

  if(filters.isRestrictedAsAccountLevel !== undefined){
    getAllSupportFilesUri.addSearch('is_restricted_as_account_level', filters.isRestrictedAsAccountLevel)
  }

  getAllSupportFilesUri.addSearch('page', filters.page)
  getAllSupportFilesUri.addSearch('size', filters.size)

  try {
    return await Vue.prototype.$http.get(getAllSupportFilesUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAllSupportFiles(accountId, filters, attemptNumber + 1)
    }

    throw error
  }
}

const uploadSupportFileByEvent = async (type, file, requestData, attemptNumber = 1) => {
  const uploadSupportFileUri = new URI(config.apiBaseUrl)
  uploadSupportFileUri.segment('rcm')
  uploadSupportFileUri.segment('v1')
  uploadSupportFileUri.segment('support-files')
  uploadSupportFileUri.segment(type)
  uploadSupportFileUri.segment('upload')

  const formData = new FormData()
  formData.append('file', file)

  const requestDataStringify = JSON.stringify(requestData)
  const requestDataBlob = new Blob([requestDataStringify], { type: 'application/json' })

  formData.append('request_data', requestDataBlob)

  try {
    return await Vue.prototype.$http.post(uploadSupportFileUri.toString(), formData)
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return uploadSupportFileByEvent(type, file, requestData, attemptNumber + 1)
    }

    throw error
  }
}

const deleteSupportFileByEvent = async (type, supportFileId, attemptNumber = 1) => {
  const deleteSupportFileUri = new URI(config.apiBaseUrl)
  deleteSupportFileUri.segment('rcm')
  deleteSupportFileUri.segment('v1')
  deleteSupportFileUri.segment('support-files')
  deleteSupportFileUri.segment(type)
  deleteSupportFileUri.segment(supportFileId)

  try {
    return await Vue.prototype.$http.delete(deleteSupportFileUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return deleteSupportFileByEvent(type, supportFileId, attemptNumber + 1)
    }

    throw error
  }
}

const getSupportDocument = async (accountId, supportDocumentId, attemptNumber = 1) => {
  const getSupportDocumentContentUri = new URI(config.apiBaseUrl)
  getSupportDocumentContentUri.segment('rcm')
  getSupportDocumentContentUri.segment('v1')
  getSupportDocumentContentUri.segment('accounts')
  getSupportDocumentContentUri.segment(accountId)
  getSupportDocumentContentUri.segment('support-documents')
  getSupportDocumentContentUri.segment(supportDocumentId)

  try {
    return await Vue.prototype.$http.get(getSupportDocumentContentUri.toString(), {
      responseType: 'arraybuffer'
    })
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getSupportDocument(accountId, supportDocumentId, attemptNumber + 1)
    }

    throw error
  }
}

const getSupportDocuments = async (accountId, payload, attemptNumber = 1) => {
  const getSupportDocumentsContentUri = new URI(config.apiBaseUrl)
  getSupportDocumentsContentUri.segment('rcm')
  getSupportDocumentsContentUri.segment('v1')
  getSupportDocumentsContentUri.segment('accounts')
  getSupportDocumentsContentUri.segment(accountId)
  getSupportDocumentsContentUri.segment('support-documents')
  getSupportDocumentsContentUri.segment('download')

  const body = decamelizeKeys(payload)

  try {
    return await Vue.prototype.$http.post(getSupportDocumentsContentUri.toString(), body, {
      responseType: 'arraybuffer'
    })
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getSupportDocuments(accountId, payload, attemptNumber + 1)
    }

    throw error
  }
}

async function getDetailCharge(chargeId, attemptNumber = 1) {
  const getAccountUri = new URI(config.apiBaseUrl)
  getAccountUri.segment('rcm')
  getAccountUri.segment('v1')
  getAccountUri.segment('charges')
  getAccountUri.segment(chargeId)

  try {
    return await Vue.prototype.$http.get(getAccountUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAccounts(attemptNumber + 1)
    }

    throw error
  }
}

async function getAuditComment(accountId, chargeId, attemptNumber = 1) {
  const getAccountUri = new URI(config.apiBaseUrl)
  getAccountUri.segment('rcm')
  getAccountUri.segment('v1')
  getAccountUri.segment('accounts')
  getAccountUri.segment(accountId)
  getAccountUri.segment('audit-comments')

  if (chargeId) {
    getAccountUri.addSearch('chargeId', chargeId)
  }

  try {
    return await Vue.prototype.$http.get(getAccountUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAuditComment(attemptNumber + 1)
    }

    throw error
  }
}

const createOrUpdateComment = async ({accountId, payload, attemptNumber = 1}) => {
  const getSupportDocumentContentUri = new URI(config.apiBaseUrl);
  getSupportDocumentContentUri.segment('rcm');
  getSupportDocumentContentUri.segment('v1');
  getSupportDocumentContentUri.segment('accounts');
  getSupportDocumentContentUri.segment(accountId);
  getSupportDocumentContentUri.segment('audit-comments');

  const body = decamelizeKeys(payload)

  try {
    return await Vue.prototype.$http.patch(getSupportDocumentContentUri.toString(), body)
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return createOrUpdateComment({accountId, payload, attemptNumber: attemptNumber + 1})
    }

    throw error
  }
}

async function getAgreements(filters, attemptNumber = 1) {
  const getAgreementsUri = new URI(config.apiBaseUrl)
  getAgreementsUri.segment('rcm')
  getAgreementsUri.segment('v1')
  getAgreementsUri.segment('provider-payer-configurations')

	if (filters.agreementCodes) {
    getAgreementsUri.addSearch('agreement_codes', filters.agreementCodes)
  }

  if (filters.payerSlugs) {
    getAgreementsUri.addSearch('payer_slugs', filters.payerSlugs)
  }

  if (filters.searchValue) {
    getAgreementsUri.addSearch('search_value', filters.searchValue)
  }

  if (filters.serviceSearchValue) {
    getAgreementsUri.addSearch('service_search_value', filters.serviceSearchValue)
  }

  if(filters.sort){
    getAgreementsUri.addSearch('sort', filters.sort)
  }

  if(filters.orderBy){
    getAgreementsUri.addSearch('order_by', filters.orderBy)
  }

  getAgreementsUri.addSearch('page', filters.page)
  getAgreementsUri.addSearch('size', filters.size)

  try {
    return await Vue.prototype.$http.get(getAgreementsUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds
    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status
    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAgreements(filters, attemptNumber + 1)
    }
    throw error
  }
}

async function getAgreementsV2(filters, attemptNumber = 1) {
  const getAgreementsUri = new URI(config.apiBaseUrl)
  getAgreementsUri.segment('rcm')
  getAgreementsUri.segment('v2')
  getAgreementsUri.segment('provider-payer-configurations')

	if (filters.agreementCodes) {
    getAgreementsUri.addSearch('agreement_codes', filters.agreementCodes)
  }

  if (filters.payerSlugs) {
    getAgreementsUri.addSearch('payer_slugs', filters.payerSlugs)
  }

  if (filters.searchValue) {
    getAgreementsUri.addSearch('search_value', filters.searchValue)
  }

  if (filters.serviceSearchValue) {
    getAgreementsUri.addSearch('service_search_value', filters.serviceSearchValue)
  }

  if(filters.sort){
    getAgreementsUri.addSearch('sort', filters.sort)
  }

  if(filters.orderBy){
    getAgreementsUri.addSearch('order_by', filters.orderBy)
  }

  getAgreementsUri.addSearch('page', filters.page)
  getAgreementsUri.addSearch('size', filters.size)

  try {
    return await Vue.prototype.$http.get(getAgreementsUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds
    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status
    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAgreements(filters, attemptNumber + 1)
    }
    throw error
  }
}

async function getAllAgreements(attemptNumber = 1) {
  const getAgreementsUri = new URI(config.apiBaseUrl)
  getAgreementsUri.segment('rcm')
  getAgreementsUri.segment('v1')
  getAgreementsUri.segment('provider-payer-configurations')
	getAgreementsUri.segment('all-agreements')

  try {
    return await Vue.prototype.$http.get(getAgreementsUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds
    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status
    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAllAgreements(attemptNumber + 1)
    }
    throw error
  }
}

async function uploadAgreementMatrix(file, processType, providerSlug, attemptNumber = 1) {
  const uploadAgreementMatrixUri = new URI(config.apiBaseUrl)
  uploadAgreementMatrixUri.segment('rcm')
  uploadAgreementMatrixUri.segment('v1')
  uploadAgreementMatrixUri.segment('provider-payer-configurations')
	uploadAgreementMatrixUri.segment('upload-process-file')

  const formData = new FormData()
  formData.append('file', file)
  formData.append('process_type', processType)
  formData.append('provider_slug', providerSlug)

  try {
    return await Vue.prototype.$http.post(uploadAgreementMatrixUri.toString(), formData)
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return uploadAgreementMatrix(file, processType, providerSlug, attemptNumber + 1)
    }throw error
  }
}

async function getAgreementMatrix(providerSlug, processId, attemptNumber = 1) {
  const getAgreementMatrixUri = new URI(config.apiBaseUrl)
  getAgreementMatrixUri.segment('rcm')
  getAgreementMatrixUri.segment('v1')
  getAgreementMatrixUri.segment('provider-payer-configurations')
  getAgreementMatrixUri.segment(providerSlug)
  getAgreementMatrixUri.segment('download-process-file')
  getAgreementMatrixUri.segment(processId)

  try {
    return await Vue.prototype.$http.get(getAgreementMatrixUri.toString(), {
      responseType: 'arraybuffer'
    })
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAgreementMatrix(providerSlug, processId, attemptNumber + 1)
    }

    throw error
  }
}

async function getProviderSupportDocuments(attemptNumber = 1) {
  const getProviderSupportDocumentsUri = new URI(config.apiBaseUrl)
  getProviderSupportDocumentsUri.segment('rcm')
  getProviderSupportDocumentsUri.segment('v1')
  getProviderSupportDocumentsUri.segment('support-files')
  getProviderSupportDocumentsUri.addSearch('sortBy', 'name')

  try {
    return await Vue.prototype.$http.get(getProviderSupportDocumentsUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds
    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status
    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getProviderSupportDocuments(attemptNumber + 1)
    }
    throw error
  }
}

async function getPayers(attemptNumber = 1) {
  const getPayersUri = new URI(config.apiBaseUrl)
  getPayersUri.segment('rcm')
  getPayersUri.segment('v1')
  getPayersUri.segment('payers')

  try {
    return await Vue.prototype.$http.get(getPayersUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds
    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status
    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getPayers(attemptNumber + 1)
    }
    throw error
  }
}

async function getAgreementConfiguration(agreementCode, attemptNumber = 1) {
  const getAgreementConfigurationUri = new URI(config.apiBaseUrl)
  getAgreementConfigurationUri.segment('rcm')
  getAgreementConfigurationUri.segment('v1')
  getAgreementConfigurationUri.segment('provider-payer-configurations')
  getAgreementConfigurationUri.segment(agreementCode)

  try {
    return await Vue.prototype.$http.get(getAgreementConfigurationUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds
    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status
    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAgreementConfiguration(agreementCode, attemptNumber + 1)
    }
    throw error
  }
}

async function updateAgreement(payload, attemptNumber = 1) {
  const updateAgreementUri = new URI(config.apiBaseUrl)
  updateAgreementUri.segment('rcm')
  updateAgreementUri.segment('v1')
  updateAgreementUri.segment('provider-payer-configurations')

  try {
    return await Vue.prototype.$http.patch(updateAgreementUri.toString(), payload)
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds
    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status
    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return updateAgreement(payload, attemptNumber + 1)
    }
    throw error
  }
}

const getHealthcarePlans = async (attemptNumber = 1) => {
  const getHealthcarePlansUri = new URI(config.apiBaseUrl)
  getHealthcarePlansUri.segment('rcm')
  getHealthcarePlansUri.segment('v1')
  getHealthcarePlansUri.segment('healthcare-plans')

  try {
    return await Vue.prototype.$http.get(getHealthcarePlansUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getHealthcarePlans(attemptNumber + 1)
    }

    throw error
  }
}

const getAgreementServiceSupportDocuments = async (filters, attemptNumber = 1) => {
  const getAgreementServiceSupportDocumentsUri = new URI(config.apiBaseUrl)
  getAgreementServiceSupportDocumentsUri.segment('rcm')
  getAgreementServiceSupportDocumentsUri.segment('v1')
  getAgreementServiceSupportDocumentsUri.segment('provider-payer-configurations')
  getAgreementServiceSupportDocumentsUri.segment(filters.agreementCode)
  getAgreementServiceSupportDocumentsUri.segment('provider-payer-services')
  getAgreementServiceSupportDocumentsUri.segment(filters.providerPayerServiceId)
  getAgreementServiceSupportDocumentsUri.segment('support-documents')

  if (filters.searchValue) {
    getAgreementServiceSupportDocumentsUri.addSearch('search_value', filters.searchValue)
  }

  if (filters.supportLevel) {
    getAgreementServiceSupportDocumentsUri.addSearch('level', filters.supportLevel)
  }

  if (filters.supportTypeSlug) {
    getAgreementServiceSupportDocumentsUri.addSearch('support_type_slug', filters.supportTypeSlug)
  }

  getAgreementServiceSupportDocumentsUri.addSearch('page', filters.page)
  getAgreementServiceSupportDocumentsUri.addSearch('size', filters.size)

  try {
    return await Vue.prototype.$http.get(getAgreementServiceSupportDocumentsUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAgreementServiceSupportDocuments(filters, attemptNumber + 1)
    }

    throw error
  }
}

const getAgreementProductSupportDocuments = async (filters, attemptNumber = 1) => {
  const getAgreementProductSupportDocuments = new URI(config.apiBaseUrl)
  getAgreementProductSupportDocuments.segment('rcm')
  getAgreementProductSupportDocuments.segment('v1')
  getAgreementProductSupportDocuments.segment('provider-payer-configurations')
  getAgreementProductSupportDocuments.segment(filters.agreementCode)
  getAgreementProductSupportDocuments.segment('provider-payer-products')
  getAgreementProductSupportDocuments.segment('support-documents')

  if (filters.providerPayerServiceId) {
    getAgreementProductSupportDocuments.addSearch(
      'provider_payer_service_id',
      filters.providerPayerServiceId
    )
  }

  if (filters.searchValue) {
    getAgreementProductSupportDocuments.addSearch('search_value', filters.searchValue)
  }

  if (filters.supportTypeSlug) {
    getAgreementProductSupportDocuments.addSearch('support_type_slug', filters.supportTypeSlug)
  }

  if(filters.lastValue) {
    getAgreementProductSupportDocuments.addSearch('last_value', filters.lastValue)
  }

  try {
    return await Vue.prototype.$http.get(getAgreementProductSupportDocuments.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAgreementProductSupportDocuments(filters, attemptNumber + 1)
    }

    throw error
  }
}

const getSupportFileTypes = async (attemptNumber = 1) => {
  const getSupportFileTypesUri = new URI(config.apiBaseUrl)
  getSupportFileTypesUri.segment('rcm')
  getSupportFileTypesUri.segment('v1')
  getSupportFileTypesUri.segment('support-files')
  getSupportFileTypesUri.segment('types')

  try {
    return await Vue.prototype.$http.get(getSupportFileTypesUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getSupportFileTypes(attemptNumber + 1)
    }

    throw error
  }
}

const getSupportFiles = async (attemptNumber = 1) => {
  const getSupportFilesUri = new URI(config.apiBaseUrl)
  getSupportFilesUri.segment('rcm')
  getSupportFilesUri.segment('v1')
  getSupportFilesUri.segment('support-files')

  try {
    return await Vue.prototype.$http.get(getSupportFilesUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getSupportFiles(attemptNumber + 1)
    }
    throw error
  }
}

const getAgreementProducts = async (payload, attemptNumber = 1) => {
  const getAgreementProductsUri = new URI(config.apiBaseUrl)
  getAgreementProductsUri.segment('rcm')
  getAgreementProductsUri.segment('v1')
  getAgreementProductsUri.segment('provider-payer-configurations')
  getAgreementProductsUri.segment(payload.agreementCode)
  getAgreementProductsUri.segment('provider-payer-products')

  if (payload.searchValue) {
    getAgreementProductsUri.addSearch('search_value', payload.searchValue)
  }

  if (payload.healthcareCoverageId) {
    getAgreementProductsUri.addSearch('healthcare_coverage_id', payload.healthcareCoverageId)
  }

  if (payload.allProductsWithService) {
    getAgreementProductsUri.addSearch('all_products_with_service', payload.allProductsWithService)
  }

  getAgreementProductsUri.addSearch('page', payload.page)
  getAgreementProductsUri.addSearch('size', payload.size)

  try {
    return await Vue.prototype.$http.get(getAgreementProductsUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAgreementProducts(payload, attemptNumber + 1)
    }

    throw error
  }
}

const changeAccountStatus = async (accountId, payload, attemptNumber = 1) => {
  const changeAccountStatusUri = new URI(config.apiBaseUrl)
  changeAccountStatusUri.segment('rcm')
  changeAccountStatusUri.segment('v1')
  changeAccountStatusUri.segment('accounts')
  changeAccountStatusUri.segment(accountId)
  changeAccountStatusUri.segment('status')

  try {
    return await Vue.prototype.$http.put(changeAccountStatusUri.toString(), payload)
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return changeAccountStatus(payload, attemptNumber + 1)
    }

    throw error
  }
}

const getProviderServiceTypes = async (providerSlug, attemptNumber = 1) => {
  const getProviderPayerServiceTypesUri = new URI(config.apiBaseUrl)
  getProviderPayerServiceTypesUri.segment('rcm')
  getProviderPayerServiceTypesUri.segment('v1')
  getProviderPayerServiceTypesUri.segment('service-types')
  getProviderPayerServiceTypesUri.segment('providers')
  getProviderPayerServiceTypesUri.segment(providerSlug)

  try{
    return await Vue.prototype.$http.get(getProviderPayerServiceTypesUri.toString())

  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response? error.response : error
    const status = response.status? response.status : error.status

    if(status === 500 && attemptNumber < maxAttempts){
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getProviderServiceTypes(providerSlug, attemptNumber + 1)
    }

    throw error
  }

}

const getProviderExecutorServices = async (providerSlug, attemptNumber = 1) => {
  const getProviderExecutorServicesUri = new URI(config.apiBaseUrl)
  getProviderExecutorServicesUri.segment('rcm')
  getProviderExecutorServicesUri.segment('v1')
  getProviderExecutorServicesUri.segment('providers')
  getProviderExecutorServicesUri.segment(providerSlug)
  getProviderExecutorServicesUri.segment('executor-services')

  try{
    return await Vue.prototype.$http.get(getProviderExecutorServicesUri.toString())

  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response? error.response : error
    const status = response.status? response.status : error.status

    if(status === 500 && attemptNumber < maxAttempts){
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getProviderExecutorServices(providerSlug, attemptNumber + 1)
    }

    throw error
  }
}

const getProviderAgreementPlanTypes = async (providerSlug, attemptNumber = 1) => {
  const getProviderAgreementPlanTypesUri = new URI(config.apiBaseUrl)
  getProviderAgreementPlanTypesUri.segment('rcm')
  getProviderAgreementPlanTypesUri.segment('v1')
  getProviderAgreementPlanTypesUri.segment('providers')
  getProviderAgreementPlanTypesUri.segment(providerSlug)
  getProviderAgreementPlanTypesUri.segment('agreement-plan-types')

  try{
    return await Vue.prototype.$http.get(getProviderAgreementPlanTypesUri.toString())

  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response? error.response : error
    const status = response.status? response.status : error.status

    if(status === 500 && attemptNumber < maxAttempts){
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getProviderAgreementPlanTypes(providerSlug, attemptNumber + 1)
    }

    throw error
  }
}

const getProviderInvoiceSources = async (providerSlug, attemptNumber = 1) => {
  const getProviderInvoiceSourcesUri = new URI(config.apiBaseUrl)
  getProviderInvoiceSourcesUri.segment('rcm')
  getProviderInvoiceSourcesUri.segment('v1')
  getProviderInvoiceSourcesUri.segment('providers')
  getProviderInvoiceSourcesUri.segment(providerSlug)
  getProviderInvoiceSourcesUri.segment('invoice-sources')

  try{
    return await Vue.prototype.$http.get(getProviderInvoiceSourcesUri.toString())

  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response? error.response : error
    const status = response.status? response.status : error.status

    if(status === 500 && attemptNumber < maxAttempts){
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getProviderInvoiceSources(providerSlug, attemptNumber + 1)
    }

    throw error
  }
}

const getPreSettlements = async (payload, attemptNumber = 1) => {
  const getPreSettlementsUri = new URI(config.apiBaseUrl)
  getPreSettlementsUri.segment('rcm')
  getPreSettlementsUri.segment('v1')
  getPreSettlementsUri.segment('pre-settlements')

  if (payload.searchValue) {
    getPreSettlementsUri.addSearch('search_value', payload.searchValue)
  }

  if (payload.statuses) {
    getPreSettlementsUri.addSearch('statuses', payload.statuses.join(','))
  }

  getPreSettlementsUri.addSearch('page', payload.page)
  getPreSettlementsUri.addSearch('size', payload.size)

  try{
    return await Vue.prototype.$http.get(getPreSettlementsUri.toString())

  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response? error.response : error
    const status = response.status? response.status : error.status

    if(status === 500 && attemptNumber < maxAttempts){
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getPreSettlements(payload, attemptNumber + 1)
    }

    throw error
  }
}

const getPreSettlementsDetail = async (originInvoiceGroupId, attemptNumber = 1) => {
  const getPreSettlementsDetailUri = new URI(config.apiBaseUrl)
  getPreSettlementsDetailUri.segment('rcm')
  getPreSettlementsDetailUri.segment('v1')
  getPreSettlementsDetailUri.segment('pre-settlements')
  getPreSettlementsDetailUri.segment(originInvoiceGroupId)

  try{
    return await Vue.prototype.$http.get(getPreSettlementsDetailUri.toString())

  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response? error.response : error
    const status = response.status? response.status : error.status

    if(status === 500 && attemptNumber < maxAttempts){
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getPreSettlementsDetail(originInvoiceGroupId, attemptNumber + 1)
    }

    throw error
  }
}

const getPreSettlementFile = async (preSettlementId, attemptNumber = 1) => {
  const getPreSettlementFileUri = new URI(config.apiBaseUrl)
  getPreSettlementFileUri.segment('rcm')
  getPreSettlementFileUri.segment('v1')
  getPreSettlementFileUri.segment('pre-settlements')
  getPreSettlementFileUri.segment(preSettlementId)
  getPreSettlementFileUri.segment('files')
  getPreSettlementFileUri.segment('download')

  try{
    return await Vue.prototype.$http.get(getPreSettlementFileUri.toString(), {
      responseType: 'arraybuffer'
    })

  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response? error.response : error
    const status = response.status? response.status : error.status

    if(status === 500 && attemptNumber < maxAttempts){
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getPreSettlementFile(preSettlementId, attemptNumber + 1)
    }

    throw error
  }
}

const getSupportFormatFileFields = async (attemptNumber = 1) =>  {
  const supportFormatFileFieldsUri = new URI(config.apiBaseUrl)
  supportFormatFileFieldsUri.segment('rcm')
  supportFormatFileFieldsUri.segment('v1')
  supportFormatFileFieldsUri.segment('provider-payer-configurations')
  supportFormatFileFieldsUri.segment('file-format-fields')

  try {
    return await Vue.prototype.$http.get(supportFormatFileFieldsUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getSupportFormatFileFields(attemptNumber + 1)
    }
    throw error
  }
}

const getProviderProducts = async (providerSlug, payload, attemptNumber = 1) => {
  const getProviderProductsUri = new URI(config.apiBaseUrl)
  getProviderProductsUri.segment('rcm')
  getProviderProductsUri.segment('v1')
  getProviderProductsUri.segment('providers')
  getProviderProductsUri.segment(providerSlug)
  getProviderProductsUri.segment('products')

  if (payload.searchValue) {
    getProviderProductsUri.addSearch('search_value', payload.searchValue)
  }

  if (payload.lastValue) {
    getProviderProductsUri.addSearch('last_value', payload.lastValue)
  }

  getProviderProductsUri.addSearch('page', payload.page)
  getProviderProductsUri.addSearch('size', payload.size)

  try {
    return await Vue.prototype.$http.get(getProviderProductsUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getProviderProducts(providerSlug, attemptNumber + 1)
    }

    throw error
  }
}

const updateProviderProducts = async (providerSlug, payload, attemptNumber = 1) => {
  const updateProviderProductsUri = new URI(config.apiBaseUrl)
  updateProviderProductsUri.segment('rcm')
  updateProviderProductsUri.segment('v1')
  updateProviderProductsUri.segment('providers')
  updateProviderProductsUri.segment(providerSlug)
  updateProviderProductsUri.segment('products')

  try {
    return await Vue.prototype.$http.put(updateProviderProductsUri.toString(), payload)
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return updateProviderProducts(payload, attemptNumber + 1)
    }

    throw error
  }
}

const getUploadedAgreementMatrix = async (providerSlug, payload, attemptNumber = 1) => {
  const getUploadedAgreementMatrixUri = new URI(config.apiBaseUrl)
  getUploadedAgreementMatrixUri.segment('rcm')
  getUploadedAgreementMatrixUri.segment('v1')
  getUploadedAgreementMatrixUri.segment('providers')
  getUploadedAgreementMatrixUri.segment(providerSlug)
  getUploadedAgreementMatrixUri.segment('bulk-process-information')

  getUploadedAgreementMatrixUri.addSearch('page', payload.page)
  getUploadedAgreementMatrixUri.addSearch('size', payload.size)

  try{
    return await Vue.prototype.$http.get(getUploadedAgreementMatrixUri.toString())

  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response? error.response : error
    const status = response.status? response.status : error.status

    if(status === 500 && attemptNumber < maxAttempts){
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getUploadedAgreementMatrix(payload, attemptNumber + 1)
    }

    throw error
  }
}

const getViewConfigurationByProviderSlug = async (providerSlug,  attemptNumber = 1) => {
  const getViewConfigurationByProviderSlugUri = new URI(config.apiBaseUrl)
  getViewConfigurationByProviderSlugUri.segment('rcm')
  getViewConfigurationByProviderSlugUri.segment('v1')
  getViewConfigurationByProviderSlugUri.segment('view-configuration')
  getViewConfigurationByProviderSlugUri.segment(providerSlug)

  try {
    return await Vue.prototype.$http.get(getViewConfigurationByProviderSlugUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getViewConfigurationByProviderSlug(providerSlug)
    }

    throw error
  }
}

const validateAccountSupportDocuments = async (accountsToVerify, attemptNumber = 1) => {
	const accountSupportValidationUri = new URI(config.apiBaseUrl)
  accountSupportValidationUri.segment('rcm')
  accountSupportValidationUri.segment('v1')
  accountSupportValidationUri.segment('accounts')
	accountSupportValidationUri.segment('validate-support-documents')

	try {
    const response = await Vue.prototype.$http.post(accountSupportValidationUri.toString(), {account_ids: accountsToVerify});
		return response?.data
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return validateAccountSupportDocuments(accountsToVerify, attemptNumber + 1)
    }

    throw error
  }
}


const updatePreSettlementFolder = async (payload, attemptNumber = 1) => {
  const updatePreSettlementFolder = new URI(config.apiBaseUrl)
  updatePreSettlementFolder.segment('rcm')
  updatePreSettlementFolder.segment('v1')
  updatePreSettlementFolder.segment('pre-settlements')
  updatePreSettlementFolder.segment('package-folder')

  try {
    return await Vue.prototype.$http.patch(updatePreSettlementFolder.toString(), payload)
  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return updatePreSettlementFolder(payload, attemptNumber + 1)
    }

    throw error
  }
}

const getAccountSupportDocumentsValidationStatus = async (processId, attemptNumber = 1) => {

	const accountSupportValidationUri = new URI(config.apiBaseUrl)
  accountSupportValidationUri.segment('rcm')
  accountSupportValidationUri.segment('v1')
  accountSupportValidationUri.segment('accounts')
	accountSupportValidationUri.segment('validate-support-documents')
	accountSupportValidationUri.addSearch('process_id', processId)

	try {
    const response = await Vue.prototype.$http.get(accountSupportValidationUri.toString());
		return response?.data
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAccountSupportDocumentsValidationStatus(processId, attemptNumber + 1)
    }

    throw error
  }
}

const getPreSettlementFolderConfiguration = async (attemptNumber = 1) => {
  const updatePreSettlementFolder = new URI(config.apiBaseUrl)
  updatePreSettlementFolder.segment('rcm')
  updatePreSettlementFolder.segment('v1')
  updatePreSettlementFolder.segment('pre-settlements')
  updatePreSettlementFolder.segment('package-folder')

  try {
    return await Vue.prototype.$http.get(updatePreSettlementFolder.toString())
  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getPreSettlementFolderConfiguration(attemptNumber + 1)
    }

    throw error
  }
}

const buildPreSettlement = async (originInvoiceGroupId, attemptNumber = 1) => {
  const buildPreSettlementUri = new URI(config.apiBaseUrl)
  buildPreSettlementUri.segment('rcm')
  buildPreSettlementUri.segment('v1')
  buildPreSettlementUri.segment('pre-settlements')
  buildPreSettlementUri.segment(originInvoiceGroupId)
  buildPreSettlementUri.segment('build')

  try{
    return await Vue.prototype.$http.put(buildPreSettlementUri.toString())

  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response? error.response : error
    const status = response.status? response.status : error.status

    if(status === 500 && attemptNumber < maxAttempts){
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return buildPreSettlement(originInvoiceGroupId, attemptNumber + 1)
    }

    throw error
  }
}

const settlementAction = async(payload, attemptNumber = 1) => {
  const settlementActionsUri = new URI(config.apiBaseUrl)
  settlementActionsUri.segment('rcm')
  settlementActionsUri.segment('v1')
  settlementActionsUri.segment('settlements')
  settlementActionsUri.segment('register-action')
  const body = decamelizeKeys(payload)

  try {
    return await Vue.prototype.$http.post(settlementActionsUri.toString(), body)
  }
  catch(error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response? error.response : error
    const status = response.status? response.status : error.status

    if(status === 500 && attemptNumber < maxAttempts){
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return settlementAction(payload, attemptNumber + 1)
    }

    throw error
  }
}

const statusPreSettlement = async(originInvoiceGroupId, payload, attemptNumber = 1) => {
  const statusPreSettlementUri = new URI(config.apiBaseUrl)
  statusPreSettlementUri.segment('rcm')
  statusPreSettlementUri.segment('v1')
  statusPreSettlementUri.segment('pre-settlements')
  statusPreSettlementUri.segment(originInvoiceGroupId)
  statusPreSettlementUri.segment('status')
  const body = decamelizeKeys(payload)

  try {
    return await Vue.prototype.$http.put(statusPreSettlementUri.toString(), body)
  }
  catch(error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response? error.response : error
    const status = response.status? response.status : error.status

    if(status === 500 && attemptNumber < maxAttempts){
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return statusPreSettlement(originInvoiceGroupId, payload, attemptNumber + 1)
    }

    throw error
  }
}

const getUsers = async (providerSlug, attemptNumber = 1) => {
  const getUsersUri = new URI(config.apiBaseUrl)
  getUsersUri.segment('rcm')
  getUsersUri.segment('v1')
  getUsersUri.segment('users')
  getUsersUri.segment(providerSlug)

  try {
    return await Vue.prototype.$http.get(getUsersUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getUsers(providerSlug, attemptNumber + 1)
    }

    throw error
  }
}

const assignAccountResponsible = async (payload, attemptNumber = 1) => {
  const assignAccountResponsibleUri = new URI(config.apiBaseUrl)
  assignAccountResponsibleUri.segment('rcm')
  assignAccountResponsibleUri.segment('v1')
  assignAccountResponsibleUri.segment('responsible')

  const body = decamelizeKeys(payload.body)

  try {
    return await Vue.prototype.$http.post(assignAccountResponsibleUri.toString(), body)
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return assignAccountResponsible(payload, attemptNumber + 1)
    }

    throw error
  }
}

const replaceSupportFileForPreSettlement = async (accountProcessSettlementGroupFileId, file, attemptNumber = 1) => {
  const replaceSupportFileForPreSettlementUri = new URI(config.apiBaseUrl)
  replaceSupportFileForPreSettlementUri.segment('rcm')
  replaceSupportFileForPreSettlementUri.segment('v1')
  replaceSupportFileForPreSettlementUri.segment('pre-settlements')
  replaceSupportFileForPreSettlementUri.segment('files')
  replaceSupportFileForPreSettlementUri.segment(accountProcessSettlementGroupFileId)
  replaceSupportFileForPreSettlementUri.segment('replace')

  const formData = new FormData()
  formData.append('file', file)

  try {
    return await Vue.prototype.$http.post(replaceSupportFileForPreSettlementUri.toString(), formData)
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return replaceSupportFileForPreSettlement(accountProcessSettlementGroupFileId, file, attemptNumber + 1)
    }

    throw error
  }
}

const cleanPackageFolder = async (payload, attemptNumber = 1) => {
  const cleanPackageFolderUri = new URI(config.apiBaseUrl)
  cleanPackageFolderUri.segment('rcm')
  cleanPackageFolderUri.segment('v1')
  cleanPackageFolderUri.segment('accounts')
  cleanPackageFolderUri.segment('clean-account-packages')

  try {
    return await Vue.prototype.$http.post(cleanPackageFolderUri.toString(), { account_ids: payload.body })
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return cleanPackageFolder(payload, attemptNumber + 1)
    }

    throw error
  }
}

export {
  getAccounts,
  getRolePermissionsStatuses,
  getRolePermissionsAccountRestrictedEvents,
  getRolePermissionsSupportFileRestrictions,
  getAccountCharges,
  getDetailAccount,
  getAllSupportFiles,
  uploadSupportFileByEvent,
  deleteSupportFileByEvent,
  getSupportDocument,
  getSupportDocuments,
  getDetailCharge,
  getAuditComment,
  createOrUpdateComment,
  getAgreementConfiguration,
  updateAgreement,
  getAgreements,
  getAgreementsV2,
	getAllAgreements,
  getProviderSupportDocuments,
  getPayers,
  getHealthcarePlans,
  getAgreementServiceSupportDocuments,
  getAgreementProductSupportDocuments,
  getSupportFileTypes,
  getAgreementProducts,
  changeAccountStatus,
  getProviderServiceTypes,
  getProviderExecutorServices,
  getProviderAgreementPlanTypes,
  getProviderInvoiceSources,
  getPreSettlements,
  getPreSettlementsDetail,
  getPreSettlementFile,
  getSupportFiles,
  getSupportFormatFileFields,
  getProviderProducts,
  updateProviderProducts,
  updatePreSettlementFolder,
  getPreSettlementFolderConfiguration,
  getViewConfigurationByProviderSlug,
  getUploadedAgreementMatrix,
  uploadAgreementMatrix,
	validateAccountSupportDocuments,
	getAccountSupportDocumentsValidationStatus,
  getAgreementMatrix,
  buildPreSettlement,
  settlementAction,
  getUsers,
  assignAccountResponsible,
  statusPreSettlement,
  replaceSupportFileForPreSettlement,
  cleanPackageFolder
}
